// Общая модалка заказа + бейджи статуса/долга. Используется на страницах
// «Заявки» (Orders) и «Заведение» (CustomerDetail), чтобы не дублировать UI.

import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Check,
  X,
  Truck,
  PackageCheck,
  Phone,
  MapPin,
  Building2,
  FileText,
  Share2,
  Loader2,
  Workflow,
} from 'lucide-react';

import {
  type SupplierOrder,
  type PurchaseOrderStatus,
  type DebtStatus,
  type PaymentTerms,
  type SupplierOrderAction,
} from '@/services/orders';
import { rub, dateTime } from '@/lib/format';
import type { SupplierProfile } from '@/services/profile';
import { downloadInvoice, shareInvoice } from '@/lib/invoice';

export const STATUS: Record<PurchaseOrderStatus, { label: string; cls: string }> = {
  NEW: { label: 'Новый', cls: 'bg-blue-50 text-blue-700' },
  CONFIRMED: { label: 'Подтверждён', cls: 'bg-indigo-50 text-indigo-700' },
  SHIPPED: { label: 'Отгружен', cls: 'bg-violet-50 text-violet-700' },
  DELIVERED: { label: 'Доставлен', cls: 'bg-emerald-50 text-emerald-700' },
  REJECTED: { label: 'Отклонён', cls: 'bg-red-50 text-red-700' },
  CANCELLED: { label: 'Отменён', cls: 'bg-ink/10 text-ink/50' },
};

export const DEBT: Record<DebtStatus, { label: string; cls: string }> = {
  UNPAID: { label: 'Не оплачен', cls: 'bg-amber-50 text-amber-700' },
  PARTIAL: { label: 'Частично', cls: 'bg-amber-50 text-amber-700' },
  PAID: { label: 'Оплачен', cls: 'bg-emerald-50 text-emerald-700' },
  OVERDUE: { label: 'Просрочен', cls: 'bg-red-50 text-red-700' },
};

const TERMS: Record<PaymentTerms, string> = {
  PREPAY: 'Предоплата',
  ON_DELIVERY: 'Оплата по факту',
  DEFERRED: 'Отсрочка',
};

export function StatusBadges({ order }: { order: SupplierOrder }) {
  const showDebt =
    order.status !== 'NEW' &&
    order.status !== 'REJECTED' &&
    order.status !== 'CANCELLED' &&
    order.paymentTerms !== 'PREPAY';
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className={`badge ${STATUS[order.status].cls}`}>{STATUS[order.status].label}</span>
      {showDebt && <span className={`badge ${DEBT[order.debtStatus].cls}`}>{DEBT[order.debtStatus].label}</span>}
    </div>
  );
}

export function OrderModal({
  order,
  supplier,
  busy,
  onClose,
  onAction,
  onPay,
}: {
  order: SupplierOrder;
  supplier: SupplierProfile | null;
  busy: boolean;
  onClose: () => void;
  onAction: (a: SupplierOrderAction) => void;
  onPay: (amount: number, note?: string) => void;
}) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [pdfBusy, setPdfBusy] = useState<null | 'pdf' | 'share'>(null);

  const handlePdf = async () => {
    if (pdfBusy) return;
    setPdfBusy('pdf');
    try {
      await downloadInvoice(order, supplier);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось сформировать PDF');
    } finally {
      setPdfBusy(null);
    }
  };

  const handleShare = async () => {
    if (pdfBusy) return;
    setPdfBusy('share');
    try {
      const res = await shareInvoice(order, supplier);
      if (res === 'downloaded') toast.success('Поделиться недоступно — накладная скачана');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось поделиться');
    } finally {
      setPdfBusy(null);
    }
  };

  const remaining = Math.max(0, order.totalCost - order.paidAmount);
  const canPay =
    order.status !== 'NEW' &&
    order.status !== 'REJECTED' &&
    order.status !== 'CANCELLED' &&
    order.paymentTerms !== 'PREPAY' &&
    order.debtStatus !== 'PAID';

  const submitPay = () => {
    const v = parseFloat(amount.replace(',', '.'));
    if (!Number.isFinite(v) || v <= 0) {
      toast.error('Введите сумму');
      return;
    }
    onPay(Math.round(v * 100), note.trim() || undefined);
    setAmount('');
    setNote('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <button className="absolute inset-0 bg-ink/40" onClick={onClose} aria-label="Закрыть" />
      <div className="relative w-full sm:max-w-lg max-h-[92vh] overflow-auto bg-white sm:rounded-2xl rounded-t-2xl">
        <div className="sticky top-0 bg-white border-b border-line px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-ink truncate">{order.establishment.name}</div>
            <div className="text-xs text-ink/45 mt-0.5">
              {dateTime(order.createdAt)} · {TERMS[order.paymentTerms]}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-ink/50 hover:text-ink shrink-0">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <StatusBadges order={order} />

          {/* Контакты заведения */}
          <div className="text-sm text-ink/70 space-y-1.5">
            <div className="flex items-center gap-2">
              <Building2 size={14} className="text-ink/40" />
              {order.establishment.name}
            </div>
            {(order.contactPhone || order.establishment.phone) && (
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-ink/40" />
                {order.contactPhone ?? order.establishment.phone}
              </div>
            )}
            {(order.deliveryAddress || order.establishment.address) && (
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-ink/40" />
                {order.deliveryAddress ?? order.establishment.address}
              </div>
            )}
            {order.comment && <div className="text-ink/60 italic">«{order.comment}»</div>}
          </div>

          {/* Позиции */}
          <div className="border border-line rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-paper text-ink/50 text-xs">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Товар</th>
                  <th className="text-right font-medium px-3 py-2">Кол-во</th>
                  <th className="text-right font-medium px-3 py-2">Сумма</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {order.items.map((it) => (
                  <tr key={it.id}>
                    <td className="px-3 py-2">
                      <div className="text-ink">{it.name}</div>
                      <div className="text-xs text-ink/45">{rub(it.price)} / {it.unit}</div>
                    </td>
                    <td className="px-3 py-2 text-right text-ink/70 whitespace-nowrap">
                      {it.quantity} {it.unit}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-ink whitespace-nowrap">
                      {rub(it.lineTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-line">
                  <td className="px-3 py-2.5 font-semibold text-ink" colSpan={2}>
                    Итого
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold text-ink">
                    {rub(order.totalCost)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Накладная */}
          <div className="flex gap-2">
            <button
              onClick={handlePdf}
              disabled={pdfBusy !== null}
              className="btn-outline flex-1 justify-center disabled:opacity-50"
            >
              {pdfBusy === 'pdf' ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
              Накладная PDF
            </button>
            <button
              onClick={handleShare}
              disabled={pdfBusy !== null}
              className="btn-outline flex-1 justify-center disabled:opacity-50"
            >
              {pdfBusy === 'share' ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
              Поделиться
            </button>
          </div>

          {/* Долг / оплаты */}
          {order.paymentTerms !== 'PREPAY' && order.status !== 'NEW' && (
            <div className="rounded-xl bg-paper border border-line p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-ink/55">Оплачено</span>
                <span className="text-ink">{rub(order.paidAmount)} из {rub(order.totalCost)}</span>
              </div>
              {remaining > 0 && (
                <div className="flex justify-between mt-1">
                  <span className="text-ink/55">Остаток долга</span>
                  <span className="font-medium text-amber-700">{rub(remaining)}</span>
                </div>
              )}
              {order.dueDate && (
                <div className="flex justify-between mt-1">
                  <span className="text-ink/55">Оплатить до</span>
                  <span className="text-ink/80">{dateTime(order.dueDate)}</span>
                </div>
              )}
              {order.payments.length > 0 && (
                <div className="mt-2 pt-2 border-t border-line space-y-1">
                  {order.payments.map((p) => (
                    <div key={p.id} className="flex justify-between text-xs text-ink/55">
                      <span>{dateTime(p.paidAt)}{p.note ? ` · ${p.note}` : ''}</span>
                      <span className="text-emerald-700">+{rub(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Форма оплаты */}
          {canPay && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-ink/50 uppercase tracking-wide">Отметить оплату</div>
              <div className="flex gap-2">
                <input
                  className="input"
                  inputMode="decimal"
                  placeholder={`Сумма, ₽ (остаток ${rub(remaining)})`}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <button onClick={submitPay} disabled={busy} className="btn-primary shrink-0 disabled:opacity-50">
                  Внести
                </button>
              </div>
              <input
                className="input"
                placeholder="Комментарий (нал / п/п №…)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}

          {/* Действия по статусу — скрыты, если заявки ведутся в 1С (см. Integrations) */}
          {supplier?.ordersManagedInErp ? (
            (order.status === 'NEW' || order.status === 'CONFIRMED') && (
              <div className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                <Workflow size={16} className="mt-0.5 shrink-0" />
                Статус меняется в 1С — ждём подтверждения/отгрузки оттуда.
              </div>
            )
          ) : (
            (order.status === 'NEW' || order.status === 'CONFIRMED') && (
              <div className="flex gap-2 pt-1">
                {order.status === 'NEW' && (
                  <>
                    <button
                      disabled={busy}
                      onClick={() => onAction('confirm')}
                      className="btn-primary flex-1 justify-center disabled:opacity-50"
                    >
                      <Check size={16} /> Подтвердить
                    </button>
                    <button
                      disabled={busy}
                      onClick={() => onAction('reject')}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-red-200 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      <X size={16} /> Отклонить
                    </button>
                  </>
                )}
                {order.status === 'CONFIRMED' && (
                  <button
                    disabled={busy}
                    onClick={() => onAction('ship')}
                    className="btn-primary flex-1 justify-center disabled:opacity-50"
                  >
                    <Truck size={16} /> Отгрузить
                  </button>
                )}
              </div>
            )
          )}

          {order.status === 'DELIVERED' && (
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <PackageCheck size={16} /> Заказ принят заведением
              {order.deliveredAt ? ` · ${dateTime(order.deliveredAt)}` : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
